import { VariantProps, cva } from 'class-variance-authority'
import React, { ButtonHTMLAttributes } from 'react'
import clsx from 'clsx'

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  loading?: boolean
  disabled?: boolean
  className?: string
  variant?:
    | 'primary'
    | 'secondary'
    | 'outlined'
    | 'custom'
    | 'primaryWithoutDisabled'
  color?: 'success' | 'warning' | 'error' | 'default'
  customSize?: 'xl' | 'lg' | 'rg' | 'sm' | 'md' | null
  width?: string
  height?: string
  borderRadius?: string
  icon?: React.ReactNode
  iconClassName?: string
}

const ButtonVariants = cva(
  `flex items-center justify-center font-medium rounded-full focus:outline-none transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`,
  {
    variants: {
      variant: {
        primary:
          'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-600/90',
        primaryWithoutDisabled:
          'bg-blue-600 text-white hover:bg-blue-700',
        secondary:
          'bg-white border-none text-blue-600 hover:bg-secondary/60 disabled:bg-neutral-100 disabled:hover:bg-neutral-100',
        outlined:
          'bg-white border-[0.5px] border-neutral-400 text-primary hover:bg-neutral-100 disabled:bg-neutral-50 disabled:hover:bg-neutral-50',
        custom: '',
      },
      customSize: {
        xl: 'px-6 py-4 text-xl',
        lg: 'px-5 py-3 text-lg',
        rg: 'px-4 py-2 text-[14px]',
        sm: 'px-3 py-1 text-sm',
        md: 'px-4 py-2 text-base',
        null: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      customSize: 'rg',
    },
  }
)

const Button: React.FC<ButtonProps> = ({
  loading,
  disabled,
  className,
  variant = 'primary',
  color,
  customSize = 'rg',
  icon,
  iconClassName,
  ...props
}) => {
  const getColorClasses = () => {
    if (variant === 'outlined' && color) {
      switch (color) {
        case 'success':
          return '!border !border-[#049913] !text-primary !bg-[#beffc1] hover:!bg-[#88FF8E]'
        case 'warning':
          return '!border !border-[#ff7f47] !text-primary !bg-[#ffe8be] hover:!bg-[#FFDA95]'
        case 'error':
          return '!border !border-[#FF0f0f] !text-primary !bg-[#ffc7c7] hover:!bg-[#FFA0A0]'
        default:
          return '!bg-background-disabled !text-placeholder-secondary !border-none disabled:cursor-not-allowed disabled:hover:!bg-neutral-100'
      }
    }
    return ''
  }

  return (
    <button
      className={clsx(
        ButtonVariants({ variant, customSize }),
        getColorClasses(),
        className
      )}
      disabled={disabled || loading}
      {...props}
    >
      {icon && <span className={clsx(iconClassName || 'mr-2')}>{icon}</span>}
      {props.children}
    </button>
  )
}

export default Button
